import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { useSession } from '@/features/auth/api/useSession'
import { useMyTasks } from '@/features/tasks/api/useMyTasks'
import { MyTaskSectionCard } from '@/features/tasks/components/MyTaskSectionCard'
import { ToPayLine } from '@/features/tasks/components/ToPayLine'
import { useFormat } from '@/i18n/useFormat'
import { can, CAP } from '@/lib/can'

/* «Мои задачи» — /tasks (S-2b.1, findings Q-10, C-3, C-7).
 *
 * The home of every reader the dashboard is not for (`homePathFor`), and a
 * sidebar stop for everyone. One card per section the SERVER served, in the
 * served order: a section is present when the reader holds its gate — the
 * capability, the row scope and "mine", all decided in SQL — and absent when
 * they do not. So this page renders and decides nothing; a held section with
 * no records says «Ничего не ждёт», and a reader who holds none is told why
 * the page is empty rather than shown a blank.
 *
 * The rows link to the records themselves. There is no «Показать все»: the
 * lists cannot filter by these rules (QW-9), so the remainder is plain text.
 *
 * Finance (S-2b.2) gets two more sections from the server — the rate approvals
 * and the payments to confirm — and one line the server does not send: «К
 * оплате», the dashboard counter's own figure, for a reader holding
 * `finance.receivables.read`. That line is mounted by capability, so nobody
 * else makes the dashboard read; and a holder whose section list is empty is
 * not told the role has nothing here, because the line is something.
 */
export function MyTasksPage() {
  const { t } = useTranslation()
  const format = useFormat()
  const { data: user } = useSession()
  const { data, isPending, isError, error, refetch } = useMyTasks()
  /* The capability `GET /api/dashboard` is guarded by, through `can()` — a
     custom role granted it gets the line too, and the superuser passes. */
  const toPay = can(user, CAP.financeReceivablesRead) ? <ToPayLine /> : null

  /* When the SERVER read the list, never this browser's clock (NFR-2,
     UX-DR-16): the "awaiting the client" cut-off is computed from that day,
     and a tab resumed from sleep would otherwise date a stale answer "now".
     The day and the hour are both read from that one instant in the viewer's
     zone — a sliced UTC day beside a local hour named yesterday until 05:00
     in Ashgabat. */
  const header = (
    <PageHeader
      eyebrow={
        data
          ? t('myTasks.eyebrow', {
              date: format.instantDateLong(data.readAt),
              time: format.time(data.readAt),
            })
          : undefined
      }
      title={t('myTasks.title')}
      subtitle={t('myTasks.subtitle')}
    />
  )

  if (isPending) {
    return (
      <>
        {header}
        {toPay}
        <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
      </>
    )
  }

  if (isError) {
    return (
      <>
        {header}
        {toPay}
        <ErrorState error={error} onRetry={() => void refetch()} />
      </>
    )
  }

  if (data.sections.length === 0) {
    if (toPay) {
      return (
        <>
          {header}
          {toPay}
        </>
      )
    }
    return (
      <>
        {header}
        <Card>
          <EmptyState
            title={t('myTasks.noSections.title')}
            description={t('myTasks.noSections.description')}
          />
        </Card>
      </>
    )
  }

  return (
    <>
      {header}
      {toPay}
      <div className="grid items-start gap-3.5 lg:grid-cols-2">
        {data.sections.map((section) => (
          <MyTaskSectionCard key={section.key} section={section} />
        ))}
      </div>
    </>
  )
}
